import React, { FC, SetStateAction, Dispatch, useEffect } from 'react'
import { FormControlLabel, Grid, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { CompanySearch, CompanyOption } from 'src/Libs/service-dashboard'
import { TextField } from 'src/Libs/inputs/formik'
import Checkbox from 'src/Libs/inputs/formik/Checkbox'
import { GrantCompanyAccessFormData } from 'src/Libs/installation-details/hooks'

const useStyles = makeStyles()(theme => ({
  checkboxContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
  },
  checkboxLabel: {
    marginRight: 0,
  },
}))

interface FormProps {
  onChangeFormValid: Dispatch<SetStateAction<boolean>>
}

/**
 * Displays form to grant access for a company to the installation
 * @param onChangeFormValid - function to change form validity status
 */
const Form: FC<FormProps> = ({ onChangeFormValid }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { setFieldValue, isValid, submitCount } = useFormikContext<GrantCompanyAccessFormData>()
  const onChangeCompany = (value: CompanyOption | null): void => {
    setFieldValue('company', value?.value ?? undefined)
  }

  useEffect(() => {
    onChangeFormValid(submitCount > 0 ? isValid : true)
  }, [isValid, submitCount, onChangeFormValid])

  return (
    <form noValidate>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <CompanySearch
            name={'company'}
            maxDisplayedResultCount={10}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onValueChange={onChangeCompany}
          />
        </Grid>
        <Grid item xs={12} className={classes.checkboxContainer}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={<Checkbox name={'readPermission'} />}
            label={t('installation.access-and-invitations.table.header.columns.can-read')}
            disabled
          />
          <FormControlLabel
            className={classes.checkboxLabel}
            control={<Checkbox name={'writePermission'} />}
            label={t('installation.access-and-invitations.table.header.columns.can-write')}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2">{t('installation.details.info.grant-access.reason-field-text')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField label={t('installation.details.info.grant-access.reason-field-title')} name={'reason'} multiline required />
        </Grid>
      </Grid>
    </form>
  )
}

export default Form
